// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, User, CheckSquare, Save } from 'lucide-react';
import AthleteTasksTab from './AthleteTasksTab';
import { useScoutAthleteNotes } from '@/hooks/useScoutAthleteNotes';
import { supabase } from '@/lib/supabase';
import { getSessionUser } from '@/lib/authSession';

interface AssignedAthlete {
  id: string;
  username: string;
  email: string;
  sport_id: string;
  grad_year?: number;
  avatar_url?: string;
  assigned_at: string;
}

interface AthleteActionDrawerProps {
  athlete: AssignedAthlete | null;
  isOpen: boolean;
  onClose: () => void;
}

const AthleteActionDrawer: React.FC<AthleteActionDrawerProps> = ({
  athlete,
  isOpen,
  onClose
}) => {
  const [notes, setNotes] = useState('');
  const { saveNotes, isLoading: isSavingNotes } = useScoutAthleteNotes(athlete?.id);

  // Load existing notes when athlete changes or drawer opens
  useEffect(() => {
    const loadExistingNotes = async () => {
      if (!athlete?.id || !isOpen) {
        setNotes('');
        return;
      }

      try {
        // Get current scout ID
        const { data: userData } = await getSessionUser();
        const scoutId = userData?.user?.id;
        
        if (!scoutId) return;

        console.log("📥 Loading notes for scout:", scoutId, "and athlete:", athlete.id);

        // Query for existing note
        const { data, error } = await supabase
          .from('scout_athlete_notes')
          .select('notes')
          .eq('scout_id', scoutId)
          .eq('athlete_id', athlete.id)
          .maybeSingle();

        if (error) {
          console.error("❌ Failed to load existing note:", error);
          return;
        }

        console.log("📄 Loaded note from DB:", data?.notes);
        
        // Set the loaded note or empty string if no note exists
        setNotes(data?.notes || '');
      } catch (err) {
        console.error("💥 Exception while loading existing note:", err);
        setNotes('');
      }
    };

    loadExistingNotes();
  }, [athlete?.id, isOpen]);

  const handleSaveNotes = async () => {
    if (athlete?.id) {
      await saveNotes(notes);
    }
  };

  if (!athlete) return null;

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader>
          <div className="flex items-center space-x-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={athlete.avatar_url} alt={athlete.username} />
              <AvatarFallback className="bg-blue-100 text-blue-600">
                {athlete.username.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <DrawerTitle>{athlete.username}</DrawerTitle>
              <p className="text-sm text-muted-foreground">{athlete.email}</p>
            </div>
          </div>
        </DrawerHeader>

        <div className="px-4 pb-4 overflow-y-auto flex-1">
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="tasks">Tasks</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Athlete Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Username: {athlete.username}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Email: {athlete.email}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Sport ID: {athlete.sport_id}</span>
                    </div>
                    {athlete.grad_year && (
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Class of {athlete.grad_year}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        Connected since {new Date(athlete.assigned_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <Badge variant="default" className="w-fit">
                    Connected
                  </Badge>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tasks">
              <AthleteTasksTab
                athleteId={athlete.id}
                athleteName={athlete.username}
              />
            </TabsContent>

            <TabsContent value="notes" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Scout Notes</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Private notes about {athlete.username} visible only to you
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="Enter your notes about this athlete..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={8}
                  />
                  <Button 
                    onClick={handleSaveNotes}
                    disabled={isSavingNotes}
                    className="w-full"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSavingNotes ? 'Saving...' : 'Save Notes'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default AthleteActionDrawer;